import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface CustomerProfile {
  email: string;
  plan: string;
  usage: UsageStats;
  api_keys: string[];
  billing_status: string;
}

interface UsageStats {
  current_month: number;
  limit: number;
  reset_date: string;
  urls_created: number;
  total_clicks: number;
}

interface URLInfo {
  short_code: string;
  destination_url: string;
  short_url: string;
  created_at: string;
  access_count: number;
  is_active: boolean;
  custom_code: boolean;
}

export const useCustomerData = () => {
  const { getIdToken } = useAuth();
  const [profile, setProfile] = useState<CustomerProfile | null>(null);
  const [urls, setUrls] = useState<URLInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  const fetchProfile = async () => {
    try {
      const token = await getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/customer/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setProfile(data);
      } else {
        // If profile doesn't exist, create default one
        setProfile({
          email: '',
          plan: 'free',
          usage: {
            current_month: 0,
            limit: 100,
            reset_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
            urls_created: 0,
            total_clicks: 0,
          },
          api_keys: [],
          billing_status: 'active',
        });
      }
    } catch (error) {
      setError('Failed to fetch profile');
    }
  };

  const fetchUrls = async () => {
    try {
      const token = await getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/customer/urls`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Ensure data is an array
        setUrls(Array.isArray(data) ? data : []);
      } else {
        // Set empty array on error
        setUrls([]);
      }
    } catch (error) {
      console.error('Failed to fetch URLs:', error);
      // Ensure urls is always an array even on error
      setUrls([]);
    }
  };

  const refreshData = async () => {
    setLoading(true);
    await Promise.all([fetchProfile(), fetchUrls()]);
    setLoading(false);
  };

  useEffect(() => {
    refreshData();
  }, []);

  return {
    profile,
    urls,
    loading,
    error,
    refreshData,
  };
};